import {
  EPermissionType,
  ECapabilityKey,
  TCapabilities,
  TPMPermissionsDB,
  TCustomSharedPermissionSetUI,
  TUserPermissionsUI,
  TNotificationKey,
  PRIMARY_CONTACT_KEY,
} from 'data/types/permissions.types';
import { ENotificationTypesDB } from 'data/types/property.types';

// --- Capability helpers: BE Set<string> ↔ FE Record<ECapabilityKey, boolean> ---

export const capabilitiesFromDb = (permissions?: string[] | null): TCapabilities =>
  Object.values(ECapabilityKey).reduce((acc, key) => {
    acc[key] = (permissions || []).includes(key);
    return acc;
  }, {} as TCapabilities);

export const capabilitiesToDb = (capabilities: TCapabilities): string[] =>
  Object.entries(capabilities)
    .filter(([, enabled]) => enabled)
    .map(([key]) => key);

// --- Notification helpers ---

export const notificationsFromDb = (notificationTypes?: string[], isPrimaryContact?: boolean): TNotificationKey[] => [
  ...((notificationTypes || []) as TNotificationKey[]),
  ...(isPrimaryContact ? [PRIMARY_CONTACT_KEY] : []),
];

export const notificationsToDb = (
  keys: TNotificationKey[]
): { notificationTypes: string[]; isPrimaryContact: boolean } => ({
  notificationTypes: keys.filter((k): k is ENotificationTypesDB => k !== PRIMARY_CONTACT_KEY),
  isPrimaryContact: keys.includes(PRIMARY_CONTACT_KEY),
});

// --- Assign permissions payload (PATCH /permissions-set) ---

export const assignPermissionsToDb = ({
  propertyManagerId,
  permissions,
}: {
  propertyManagerId: string;
  permissions: TUserPermissionsUI;
}): {
  propertyManagerId: string;
  permissionId?: string;
  type: EPermissionType;
  permissions?: string[];
} => ({
  propertyManagerId,
  ...('setId' in permissions && permissions.setId && { permissionId: permissions.setId }),
  type: permissions.type,
  ...(permissions.type === EPermissionType.CUSTOM_INDIVIDUAL && {
    permissions: capabilitiesToDb(permissions.capabilities),
  }),
});

// --- Position helpers: BE string[] ↔ FE { value, label }[] ---

export const positionsFromDb = (positions?: string[]): { value: string; label: string }[] =>
  (positions || []).map((pos) => ({ value: pos, label: pos }));

export const positionsToDb = (positions: { value: string; label: string }[]): string[] =>
  positions.map((pos) => pos.label);

// --- PMPermissionsDTO → TUserPermissionsUI (for profile/details/default responses) ---

export const userPermissionsFromDb = (data: TPMPermissionsDB): TUserPermissionsUI => {
  switch (data.type) {
    case EPermissionType.FULL:
      return { type: EPermissionType.FULL };
    case EPermissionType.READ:
      return { type: EPermissionType.READ };
    case EPermissionType.CUSTOM_INDIVIDUAL:
      return {
        type: EPermissionType.CUSTOM_INDIVIDUAL,
        setId: data.id,
        capabilities: capabilitiesFromDb(data.permissions),
      };
    case EPermissionType.CUSTOM_SHARED:
      return {
        type: EPermissionType.CUSTOM_SHARED,
        setId: data.id,
        setName: data.name || '',
        capabilities: capabilitiesFromDb(data.permissions),
      };
  }
};

// --- PMPermissionsDTO → TCustomSharedPermissionSetUI (for permission sets list) ---

export const customSharedFromDb = (data: TPMPermissionsDB): TCustomSharedPermissionSetUI => ({
  id: data.id,
  name: data.name || '',
  capabilities: capabilitiesFromDb(data.permissions),
  positions: positionsFromDb(data.positions),
  notifications: notificationsFromDb(data.notificationTypes, data.isPrimaryContact),
  linkedMembers: data.linkedMembers || [],
  animalUpdateConfig: data.animalUpdateConfig ?? null,
  isReportingEnabled: data.isReportingEnabled,
});

// --- FE → BE payloads for permission sets ---

export const customSharedToDb = (data: Omit<TCustomSharedPermissionSetUI, 'id' | 'linkedMembers'>) => {
  const { notificationTypes, isPrimaryContact } = notificationsToDb(data.notifications);
  const isAnimalActivitySummaryEnabled = notificationTypes.includes(ENotificationTypesDB.ANIMAL_ACTIVITY_SUMMARY);
  return {
    name: data.name,
    permissions: capabilitiesToDb(data.capabilities),
    positions: positionsToDb(data.positions),
    notificationTypes,
    isPrimaryContact,
    animalUpdateConfig: isAnimalActivitySummaryEnabled ? data.animalUpdateConfig ?? null : null,
  };
};

export const customSharedUpdateToDb = (data: TCustomSharedPermissionSetUI) => {
  const { notificationTypes, isPrimaryContact } = notificationsToDb(data.notifications);
  const isAnimalActivitySummaryEnabled = notificationTypes.includes(ENotificationTypesDB.ANIMAL_ACTIVITY_SUMMARY);
  return {
    id: data.id,
    name: data.name,
    permissions: capabilitiesToDb(data.capabilities),
    positions: positionsToDb(data.positions),
    notificationTypes,
    isPrimaryContact,
    linkedMembers: data.linkedMembers,
    type: EPermissionType.CUSTOM_SHARED,
    animalUpdateConfig: isAnimalActivitySummaryEnabled ? data.animalUpdateConfig ?? null : null,
  };
};

// --- Default permission converter (POST /permissions/default) ---
// Default uses same TPMPermissionsDB shape as all other endpoints

export const defaultPermissionToDb = (data: TUserPermissionsUI): Partial<TPMPermissionsDB> => {
  return {
    type: data.type,
    ...('setId' in data && data.setId && { id: data.setId }),
    ...('capabilities' in data && { permissions: capabilitiesToDb(data.capabilities) }),
  };
};
